<template>
  <el-row
    v-loading.fullscreen.lock="fullscreenLoading"
    class="pre-wrap"
  >
    <h3>
      {{ $t('preCheckIn.personalInfo.title') }}
    </h3>
    <el-form
      :model="userData"
      :rules="rules"
      ref="personalInfoForm"
      class="personal-info pre-reg"
      label-position="top"
    >
      <el-row class="block">
        <el-row class="title">
          {{ $t('preCheckIn.personalInfo.formTitle') }}
        </el-row>
        <el-form-item
          prop="userPhoto"
          class="photo-wrap"
          v-if="typeof this.userData.userPhoto !== 'undefined'"
        >
          <el-upload
            accept=".jpeg,.jpg,.png"
            drag
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            :on-change="savePersonalPhoto"
            :class="{'loaded-photo': isLoaded}"
          >
            <img
              v-if="isLoaded"
              :src="this.userData.userPhoto"
              class="image"
            >
            <div
              v-if="!isLoaded"
              class="no-image"
            />
            <el-button
              type="upload-photo"
              :on-click="savePersonalPhoto"
              v-show="!isLoaded"
            >
              {{ $t('preCheckIn.personalInfo.upload') }}
            </el-button>
            <el-button
              type="change-photo"
              :on-click="savePersonalPhoto"
              v-show="isLoaded"
            >
              {{ $t('preCheckIn.personalInfo.change') }}
            </el-button>
          </el-upload>
        </el-form-item>
        <el-row class="data">
          <el-form-item
            :label="$t('common.labels.firstName')"
            prop="firstName"
            :class="[{'focus': userData.firstName}, 'is-required']"
          >
            <el-input
              type="text"
              v-model="userData.firstName"
              :value="userData.firstName"
            />
          </el-form-item>
          <el-form-item
            :label="$t('common.labels.lastName')"
            prop="lastName"
            :class="[{'focus': userData.lastName}, 'is-required']"
          >
            <el-input
              type="text"
              v-model="userData.lastName"
              :value="userData.lastName"
            />
          </el-form-item>
        </el-row>
        <el-row
          class="dob"
        >
          <el-form-item
            :label="$t('common.labels.dob')"
            prop="dob"
            class="is-required"
          >
            <DobPicker
              :minage="18"
              :maxage="84"
              :birthdate.sync="userData.dob"
              ref="dobPicker"
            />
          </el-form-item>
        </el-row>
        <el-row
          v-if="showGender"
          class="gender"
        >
          <el-form-item
            :label="$t('common.labels.gender')"
            prop="gender"
          >
            <el-radio-group
              v-model="userData.sex"
              class="radio-btn"
            >
              <el-radio-button
                label="M"
              >
                {{ $t('common.labels.male') }}
              </el-radio-button>
              <el-radio-button
                label="F"
              >
                {{ $t('common.labels.female') }}
              </el-radio-button>
            </el-radio-group>
          </el-form-item>
        </el-row>
      </el-row>
      <el-form-item class="submit-container">
        <el-form-item class="submit-btn">
          <el-button
            type="primary"
            @click="submitForm('personalInfoForm')"
            :disabled="!isLoaded"
          >
            {{ $t('common.buttons.next') }}
          </el-button>
        </el-form-item>
      </el-form-item>
    </el-form>
  </el-row>
</template>

<script>
import store from "../../store";
import DobPicker from '../components/DobPicker';

export default {
  name: 'PersonalInfo',
  components: {
    DobPicker
  },
  data: () => {
    return {
      user: {},
      fullscreenLoading: false,
      isLoaded: false,
      dob: '',
      birthDate: '',
      patientNumber: null,
      activeStep: null,
      showGender: false,
      insurance: '',
      sid: null,
      photoUploadCount: 0,
      routerName: 'InsuranceData',
      rules: {},
      fields: ['firstName', 'lastName', 'dob']
    };
  },
  computed: {
    userData () {
      return this.user;
    }
  },
  mounted() {
    this.showGender = process.env.VUE_APP_SERVICE_TYPE !== 'F';
  },
  watch: {
    'userData.firstName': function (val) {
      if (val.trim().length) this.changeField({fname: val});
    },
    'userData.lastName': function (val) {
      if (val.trim().length) this.changeField({lname: val});
    },
    'userData.dob': function (val) {
      if (val.trim().length) this.changeField({dob: this.formattedDate(val)});
    },
    'userData.sex': function (val) {
      if (val.trim().length) this.changeField({sex: val});
    },
  },
  created () {
    this.fullscreenLoading = true;
    this.setProgressBar();
    this.setRules(this.fields);
    this.activeStep = this.$route.path.substring(1).split('/')[1];
    this.patientNumber = store.getters.patientData.patientNumber;
    this.sid = this.getSessionId();
    if (!this.patientNumber) {
      this.getDataByPatientNumber().then(() => {
        this.patientNumber = store.getters.patientData.patientNumber;
        this.setData();
      }).catch(() => {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
      });
    } else {
      this.setData();
    }
  },
  methods: {
    setData() {
      this.fullscreenLoading = true;
      this.user = store.getters.patientData;
      this.$set(this.userData, 'userPhoto', '');
      this.saveStatisticData(this.sid, {
        active_step: this.activeStep,
        render_PersonalInfo : 'true'
      });
      this.updateUserAppData(this.userData.patientId, {
        active_pr_step: this.activeStep,
        active_route: this.$route.name
      }).then((response)=> {
        if (response.data.isSuccess) {
          this.$axios.post(process.env.VUE_APP_INTERNAL_API + '/api/getUserData',{
            pid: this.userData.patientId
          }).then((response) => {
            if (response.data) {
              const { driverLicenceData } = response.data.appData;

              let dob = driverLicenceData?.dob || this.userData.dateOfBirth;
              let firstName = driverLicenceData?.fname || this.userData.firstName;
              let lastName = driverLicenceData?.lname || this.userData.lastName;

              this.$set(this.userData, 'dob', dob);
              this.$set(this.userData, 'firstName', firstName);
              this.$set(this.userData, 'lastName', lastName);

              this.$refs.dobPicker.changeDateOfBirth(dob);
              this.$set(this, 'insurance', response.data.appData.insurance);
              this.getProfileImage(false).then(() => {
                this.fullscreenLoading = false;
              });
            }
          }).catch(() => {
            this.setMessage(this.$t('common.errors.data'));
            this.fullscreenLoading = false;
          });
        } else {
           this.fullscreenLoading = false;
        }
      });
    },
    getProfileImage(showMsg = true) {
      this.fullscreenLoading = true;
      return this.$axios('/Patient/GetPatientProfileImage', {
        method: 'GET',
        params: {
          patientNumber: this.patientNumber
        },
        responseType: 'arraybuffer'
      }).then((response) => {
        if (response.data && response.data.byteLength !== 0) {
          const imgStr = new Buffer.from(response.data, 'binary').toString('base64'),
                prefix = "data:" + response.headers["content-type"] + ";base64,";

          this.$set(this.userData, 'userPhoto', prefix + imgStr);
          this.isLoaded = true;
          this.photoUploadCount++;
          this.saveStatisticData(this.sid, {
            photo_upload: this.photoUploadCount
          });
          if (showMsg) this.setMessage(this.$t('common.success.photo'), 'success');
        } else {
          if (response.data.errorMessage) {
            this.$message.warning(response.data.errorMessage);
          }
          this.$set(this.userData, 'userPhoto', '');
          this.isLoaded = false;
        }
        this.fullscreenLoading = false;
      });
    },
    savePersonalPhoto(file) {
      this.fullscreenLoading = true;
      const data = new FormData();
      data.append('blobData', file.raw);
      data.append('patientNumber', this.patientNumber);
      return this.$axios('/Patient/SavePatientProfileImage', {
        method: 'POST',
        headers: { 'content-type': 'multipart/form-data' },
        data: data
      }).then((response) => {
        if (response.data) {
          if (!response.data.length) {
            if (!response.data.isSuccess) {
              this.fullscreenLoading = false;
              this.$message.warning(response.data.errorMessage);
              this.$set(this.userData, 'userPhoto', '');
              this.isLoaded = false;
            }
          } else {
            this.getProfileImage();
          }
        }
      });
    },
    savePatientData() {
      return this.updatePatientInfoData({
        patientId: this.userData.patientId,
        patientNumber: this.patientNumber,
        firstName: this.userData.firstName,
        lastName: this.userData.lastName,
        dateOfBirth: this.userData.dob,
        cellPhone: this.userData.cellPhone,
        sex: this.userData.sex,
      }).then((response) => {
        if (response.data.isSuccess) {
          store.dispatch('setPatientData', this.userData);
          let stepsData = {[this.activeStep]: 1};
          this.updateStepsData(this.userData.patientId, stepsData).then((response) => {
            if (response.data.isSuccess) {
              this.updateUserAppData(this.userData.patientId,{
                fname: this.userData.firstName,
                lname: this.userData.lastName,
                dob: this.userData.dob,
                gender: this.userData.sex
              }).then((response) => {
                if (response.data.isSuccess) {
                  if (this.file) {
                    this.savePersonalPhoto().then((response) => {
                      if (response) {
                        this.saveStatisticData(this.sid, {
                          photo_save: 'true'
                        });
                        this.setMessage(this.$t('common.success.photo'), 'success');
                      }
                      this.fullscreenLoading = false;
                      this.$router.push({
                        name: this.routerName,
                        params: {
                          patientNumber: this.patientNumber
                        },
                      });
                    }).catch(() => {
                      this.fullscreenLoading = false;
                      this.setMessage(this.$t('common.errors.photo'));
                    });
                  } else {
                    this.fullscreenLoading = false;
                    this.$router.push({
                      name: this.routerName,
                      params: {
                        patientNumber: this.patientNumber
                      },
                    });
                  }
                } else {
                  this.fullscreenLoading = false;
                  this.setMessage(
                    this.$t('common.errors.update', { type: this.$t('preCheckIn.personalInfo.title').toLowerCase() }),
                    'warning'
                  );
                }
              }).catch(() => {
                this.setMessage(this.$t('common.errors.update', { type: this.$t('preCheckIn.personalInfo.title').toLowerCase() }));
                this.fullscreenLoading = false;
              });
            } else {
              this.fullscreenLoading = false;
              this.setMessage(this.$t('common.errors.fail'));
            }
          });
        }
      }).catch(() => {
        this.fullscreenLoading = false;
        this.setMessage(this.$t('common.errors.fail'));
      });
    },
    submitForm(formName) {
      this.fullscreenLoading = true;
      this.$refs[formName].validate((valid) => {
        if (valid) {
          this.saveStatisticData(this.sid, {
            submit_PersonalInfo: 'true'
          });
          this.savePatientData();
        } else {
          this.fullscreenLoading = false;
          return false;
        }
      });
    }
  }
};
</script>
